import "./userdetailsinterview.css";
import { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import {
    NotificationContainer,
    NotificationManager,
} from "react-notifications";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import emailVerificationResponse from "../../apis/otp/emailVerification";
import verifyOtpResponse from "../../apis/otp/verifyOtp";
import recruiter_header_logo from "../../components/basicInfoForm/images/logo_recruiter_header.png";

import OTP from "../../components/otp/otp";
import LoginForm from "../../components/loginform/loginmainform";
import { millisToMinutesAndSeconds } from "../../utilities/validations";
import "../candidateSignIn/candidateSignIn.css";
import "../../components/resetpassword/resetpassword.css";
import getJobDescriptionById from "../../apis/candidatescreening/getjdbyid";
import InterviewLinkExpiry from "../../components/expirylink/expirylink"
import configJson from "../../Config.json";


export default function UserDetails() {
    const location = useLocation();
    const { id: jdId } = useParams();
    const [searchParams] = useSearchParams();
    const togglevetScreen = searchParams.get("int") === "vet";
    const [step, setStep] = useState(1);
    const [details, setDetails] = useState({ jdId });
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [counter, setCounter] = useState(0);
    const navigate = useNavigate();
    const [jobData, setJobData] = useState(null);
    const [isExpired, setIsExpired] = useState(false);
    const [jdLoading, setJdLoading] = useState(true);
    const [isVET, setIsVET] = useState(togglevetScreen);
    const [userLocation, setUserLocation] = useState(null);

    useEffect(() => {
        let intervalId;
        if (counter > 0) {
            intervalId = setInterval(() => {
                setCounter((prevCounter) => prevCounter - 1000);
            }, 1000);
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [counter]);

    const jobdesbyId = async () => {
        try {
            setJdLoading(true);
            if (!togglevetScreen) {
                const response = await getJobDescriptionById(jdId);
                const jd = response.data.data;

                setJobData(jd);

                if (jd?.expiryAt) {
                    const expiryDate = new Date(jd.expiryAt);
                    const now = new Date();
                    if (expiryDate < now) {
                        setIsExpired(true);
                    }
                }
            }
        } catch (err) {
            console.error("Error fetching JD:", err?.response?.data || err);
        } finally {
            setJdLoading(false);
        }
    };

    useEffect(() => {
        if (jdId) jobdesbyId();
    }, [jdId]);

    const validate = async ({ email, name, firstName, lastName, phoneNumber, countryCallingCode, phoneCode, countryCode }) => {
        const resolvedCountryCallingCode = countryCallingCode || phoneCode || "";

        if (countryCode) {
            try {
                const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
                setUserLocation({
                    code: countryCode,
                    name: regionNames.of(countryCode),
                    dialCode: resolvedCountryCallingCode,
                });
            } catch (error) {
                console.error("Failed to build location from country code:", error);
            }
        }

        if (isVET) {
            if (!firstName || !lastName || !email) {
                NotificationManager.error("Please provide first name, last name and email", "Error", 10000);
                return;
            }
        } else {
            if (!name || !email) {
                NotificationManager.error("Please provide name and email", "Error", 10000);
                return;
            }
        }

        try {
            if (isVET) {
                updateDetails("firstName", firstName);
                updateDetails("lastName", lastName);
            } else {
                updateDetails("name", name);
            }
            updateDetails("email", email);
            if (phoneNumber) updateDetails("phoneNumber", phoneNumber);
            if (resolvedCountryCallingCode) updateDetails("countryCallingCode", resolvedCountryCallingCode);
            await sendOtp(email, phoneNumber)
        }
        catch (err) {
            console.error("Error validating user details: ", err?.response?.data || err);
            NotificationManager.error(err?.response?.data?.error || "Error. Please try again.", "Error", 10000);
            return;
        }
    }

    const updateDetails = (field, value) => {
        setDetails((elem) => ({ ...elem, [field]: value }));
    };

    const sendOtp = async (emailAddress, phone_number) => {
        const emailToUse = emailAddress || details.email;
        const phoneNumber = phone_number || details?.phoneNumber;

        if (!emailToUse) {
            NotificationManager.error("Please provide an email", "Error", 5000);
            return;
        }

        try {
            const payload = {
                email: emailToUse,
                isVerified: false,
                isInterview: !togglevetScreen,
                isVET: isVET,
                jdId: jdId,
                phoneNumber: phoneNumber || "",

            };
            const { data } = await emailVerificationResponse(payload);
            if (data?.message !== "OTP send successfully") {
                throw new Error("OTP sending failed");
            }

            updateDetails("emailVerificationId", data.id);

            setStep(2);
            setCounter(300000); // 5 minutes
            NotificationManager.success("OTP sent", "Success", 5000);
        } catch (err) {
            console.error("sendOtp error", err?.response?.data || err);
            if (err?.response?.data?.message) {
                NotificationManager.error(err?.response?.data?.message, "Error", 5000);
            }
            else {
                NotificationManager.error("Failed to send OTP || user email already exists", "Error", 5000);
            }

        }
    };

    const resendOtp = async () => {
        if (!details.email) {
            NotificationManager.error("Please provide an email", "Error", 5000);
            return;
        }

        try {
            setResendLoading(true);
            const payload = {
                email: details.email,
                isVerified: false,
                isInterview: true,
            };
            const { data } = await emailVerificationResponse(payload);
            if (data?.message !== "OTP send successfully") {
                throw new Error("OTP sending failed");
            }

            updateDetails("emailVerificationId", data.id);
            updateDetails("otp", "");
            setCounter(300000); // 5 minutes
            NotificationManager.success("OTP resent", "Success", 5000);
        } catch (err) {
            console.error("resendOtp error", err?.response?.data || err);
            NotificationManager.error("Failed to resend OTP", "Error", 5000);
        } finally {
            setResendLoading(false);
        }
    };

    const verify = async () => {
        if (!details.otp || details.otp.length !== 6) {
            NotificationManager.error("Enter a 6-digit OTP", "Error", 4000);
            return;
        }
        try {
            setLoading(true);
            const payload = {
                id: details.emailVerificationId,
                email: details.email,
                otpHash: details.otp,
            };
            const resp = await verifyOtpResponse(payload);

            if (resp.data?.message?.includes("Invalid OTP")) {
                NotificationManager.error("Invalid OTP", "Error", 5000);
                setLoading(false);
                updateDetails("otp", "");
                return;
            }

            setLoading(false);
            if (isVET) {
                navigate("/test-landing", {
                    state: {
                        isAIInterview: false,
                        userLocation: userLocation,
                        isVet: true,
                        jdId,
                        assessmentDurationInSeconds: configJson.aiinterviewduration,
                        email: details.email,
                        firstName: details.firstName,
                        lastName: details.lastName,
                        phoneNumber: details.phoneNumber || "",
                        countryCallingCode: details.countryCallingCode || "",
                        testName: "Vet interview",
                    },
                });
            } else {
                navigate("/test-landing", {
                    state: {
                        isAIInterview: true,
                        userLocation: userLocation,
                        jdId,
                        assessmentDurationInSeconds: jobData?.assessmentDurationInSeconds ?? configJson.aiinterviewduration,
                        email: details.email,
                        name: details.name,
                        phoneNumber: details.phoneNumber || "",
                        countryCallingCode: details.countryCallingCode || "",
                        testName: "AI Interview",
                    },
                });
            }
        } catch (err) {
            console.error("verify error", err?.response?.data || err);
            NotificationManager.error(" user update failed", "Error", 5000);
            setLoading(false);
            updateDetails("otp", "");
            setStep(1);
        }
    };

    const handleBackToLogin = () => {
        setStep(1);
        updateDetails("otp", "");
        updateDetails("email", "");
        updateDetails("name", "");
        setCounter(0);
    };

    if (jdLoading) {
        return (
            <div className="interview-wrapper">
                <CircularProgress />
            </div>
        );
    }

    if (isExpired) {
        return <InterviewLinkExpiry />;

    }

    return (
        <>
            <div className="nav_bg_home home-specific-header">
                <div className="container w1200 nav_bg_home_section">
                    <div className="nav_bg_left">

                        <li className='cp headerListStyleNone' >
                            <span><img src={recruiter_header_logo} className="nav_logo_home" alt='img' /></span>
                        </li>

                    </div>
                    <div className="nav_bg_right">
                        <ul className="navbar_home">
                            <li className="special_hover cp" ><span>Contact Us</span></li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="interview-wrapper">
                {step === 1 ? (
                    <LoginForm
                        isInterviewMode={true}
                        onInterviewSubmit={validate}
                        isVET={isVET}
                    />
                ) : (
                    <div className='login_container login-box-border reset-password-modern'>
                        <div className='candidate-signin-title'>
                            <div className='verify-otp-header'>
                                <div>
                                    <p className='fs-semi-bold fs-22'>Verify OTP</p>
                                    <p className='reset-subtitle'>Enter the OTP sent to your email</p>
                                </div>
                                <span
                                    className="back-to-login-link"
                                    onClick={handleBackToLogin}
                                >
                                    Back to Login
                                </span>
                            </div>
                        </div>

                        <div className='reset-password-content'>
                            <div className='form-group-reset'>
                                <label className="field_name_reset" htmlFor="email">
                                    Email
                                </label>
                                <div className='fp-email-wrapper'>
                                    <div className='fp-email-with-err'>
                                        <input
                                            type="text"
                                            id="email"
                                            className="password-input-reset"
                                            value={details?.email}
                                            disabled
                                        />
                                    </div>
                                    <div className='formone-otpbtnswitch'>
                                        {counter > 0 ? (
                                            <button type="button" disabled>
                                                {millisToMinutesAndSeconds(counter)}
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={resendOtp}
                                                disabled={resendLoading}
                                            >
                                                {resendLoading ? 'Sending...' : 'Resend OTP'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt_20">
                                <OTP
                                    otp={details.otp}
                                    onChange={(value) => updateDetails("otp", value)}
                                    label="OTP"
                                    wrapperClassName="otp-box-wrapper-verification"
                                />
                            </div>

                            <div className='reset-password-btn-container'>
                                <button
                                    type="button"
                                    className="btn-reset-password-modern full-width"
                                    onClick={verify}
                                    disabled={loading || !details.otp || details.otp.length !== 6}
                                >
                                    {loading ? (
                                        <span className="verify-btn-content">
                                            Verifying...
                                            <CircularProgress
                                                size={20}
                                                color="inherit"
                                                className="verify-btn-spinner"
                                            />
                                        </span>
                                    ) : (
                                        'Verify OTP'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <NotificationContainer />
        </>
    );
}
